/**
 * vertical-video.js
 * Extracted from sections/vertical-video.liquid inline script.
 * Handles video carousel: lazy loading, intersection-based autoplay,
 * drag-to-scroll, keyboard navigation, and theme editor integration.
 */
(function () {
  'use strict';

  var DRAG_THRESHOLD = 5;
  var SCROLL_AMOUNT = 300;

  function init(SECTION_ID) {
    var section = document.getElementById('vv-section-' + SECTION_ID);
    if (!section) return;

    var carousel = document.getElementById('vv-carousel-' + SECTION_ID);
    if (!carousel) return;

    // Personalization: reorder carousel if ?product= param matches a card
    if (typeof NDPersonalization !== 'undefined') {
      var personalizeHandle = NDPersonalization.getProductHandle();
      if (personalizeHandle) {
        NDPersonalization.reorderByHandle(carousel, '.vv-card', personalizeHandle);
      }
    }

    var controller = new AbortController();
    var signal = controller.signal;

    var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    // --- Intersection Observer: lazy-load src + play/pause by visibility ---
    var videos = carousel.querySelectorAll('.vv-video');
    var loadedVideos = new Set();

    // --- Play button: poster-first approach ---
    var intersectingVideos = new Set();
    var confirmedPlaying = new Set();
    var prevTimes = new Map();

    function showPlayBtn(video) {
      var card = video.closest('.vv-card');
      if (card) {
        card.setAttribute('data-show-play-btn', '');
        card.removeAttribute('data-video-playing');
      }
      prevTimes.delete(video);
      confirmedPlaying.delete(video);
    }

    function hidePlayBtn(video) {
      var card = video.closest('.vv-card');
      if (card) {
        card.removeAttribute('data-show-play-btn');
        card.setAttribute('data-video-playing', '');
      }
      confirmedPlaying.add(video);
    }

    videos.forEach(function (video) {
      video.addEventListener('timeupdate', function () {
        var prev = prevTimes.get(video);
        var curr = video.currentTime;
        prevTimes.set(video, curr);
        if (prev !== undefined && curr !== prev) {
          hidePlayBtn(video);
        }
      }, { signal: signal });

      video.addEventListener('pause', function () {
        if (intersectingVideos.has(video)) showPlayBtn(video);
      }, { signal: signal });

      video.addEventListener('loadeddata', function () {
        if (intersectingVideos.has(video) && !confirmedPlaying.has(video)) {
          showPlayBtn(video);
        }
      }, { signal: signal });
    });

    // Play button click
    carousel.querySelectorAll('.vv-play-btn').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        e.preventDefault();
        var card = btn.closest('.vv-card');
        var video = card ? card.querySelector('.vv-video') : null;
        if (video) video.play().catch(function () {});
      }, { signal: signal });
    });

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target;

          if (entry.isIntersecting) {
            intersectingVideos.add(video);

            if (!loadedVideos.has(video)) {
              var source = video.querySelector('source');
              if (source && source.dataset.src) {
                source.src = source.dataset.src;
                video.load();
                loadedVideos.add(video);
              }
            }

            if (!prefersReducedMotion.matches) {
              video.play().catch(function () {});
              if (video.readyState >= 2 && !confirmedPlaying.has(video)) {
                showPlayBtn(video);
              }
            }
          } else {
            intersectingVideos.delete(video);
            if (!video.paused) video.pause();
            var outCard = video.closest('.vv-card');
            if (outCard) outCard.removeAttribute('data-show-play-btn');
            prevTimes.delete(video);
          }
        });
      },
      { root: null, rootMargin: '0px', threshold: 0.3 }
    );

    videos.forEach(function (video) {
      observer.observe(video);
    });

    // --- Reduced motion ---
    function onMotionChange() {
      videos.forEach(function (video) {
        if (prefersReducedMotion.matches && !video.paused) {
          video.pause();
        }
      });
    }

    if (prefersReducedMotion.addEventListener) {
      prefersReducedMotion.addEventListener('change', onMotionChange, { signal: signal });
    }

    // --- Video error handling ---
    videos.forEach(function (video) {
      video.addEventListener(
        'error',
        function () {
          // On error, keep the poster image visible
        },
        { signal: signal }
      );
    });

    // --- Mouse drag to scroll ---
    var isDown = false;
    var startX = 0;
    var startScrollLeft = 0;
    var dragDistance = 0;

    carousel.addEventListener(
      'mousedown',
      function (e) {
        isDown = true;
        dragDistance = 0;
        startX = e.pageX - carousel.offsetLeft;
        startScrollLeft = carousel.scrollLeft;
        carousel.classList.add('active');
      },
      { signal: signal }
    );

    carousel.addEventListener(
      'mousemove',
      function (e) {
        if (!isDown) return;
        e.preventDefault();
        var x = e.pageX - carousel.offsetLeft;
        var walk = x - startX;
        dragDistance = Math.abs(walk);
        carousel.scrollLeft = startScrollLeft - walk;
      },
      { signal: signal }
    );

    carousel.addEventListener(
      'mouseup',
      function () {
        isDown = false;
        carousel.classList.remove('active');
      },
      { signal: signal }
    );

    carousel.addEventListener(
      'mouseleave',
      function () {
        isDown = false;
        carousel.classList.remove('active');
      },
      { signal: signal }
    );

    // --- Card click: navigate to product ---
    carousel.querySelectorAll('.vv-card').forEach(function (card) {
      var video = card.querySelector('.vv-video');

      if (video) {
        video.addEventListener(
          'play',
          function () {
            card.setAttribute('data-video-started', 'true');
            card.setAttribute('data-ignore-clicks', 'true');
            setTimeout(function () {
              card.removeAttribute('data-ignore-clicks');
            }, 500);
          },
          { signal: signal }
        );
      }

      card.addEventListener(
        'click',
        function () {
          if (dragDistance > DRAG_THRESHOLD) return;

          if (video && (video.paused || !confirmedPlaying.has(video))) {
            video.play().catch(function () {});
            return;
          }

          if (
            card.getAttribute('data-video-started') === 'true' &&
            card.getAttribute('data-ignore-clicks') !== 'true'
          ) {
            var url = card.getAttribute('data-product-url');
            if (url) {
              window.location.href = url;
            }
          }
        },
        { signal: signal }
      );
    });

    // --- Keyboard navigation ---
    carousel.addEventListener(
      'keydown',
      function (e) {
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          carousel.scrollBy({ left: SCROLL_AMOUNT, behavior: 'smooth' });
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          carousel.scrollBy({ left: -SCROLL_AMOUNT, behavior: 'smooth' });
        }
      },
      { signal: signal }
    );

    // --- Pageshow: resume videos on bfcache restore ---
    window.addEventListener(
      'pageshow',
      function (event) {
        if (event.persisted && !prefersReducedMotion.matches) {
          videos.forEach(function (video) {
            if (!video.paused) return;
            observer.unobserve(video);
            observer.observe(video);
          });
        }
      },
      { signal: signal }
    );

    // --- Visibility change: resume videos after iOS lock/unlock ---
    document.addEventListener(
      'visibilitychange',
      function () {
        if (document.visibilityState !== 'visible') return;
        if (prefersReducedMotion.matches) return;

        videos.forEach(function (video) {
          if (!loadedVideos.has(video)) return;
          if (!intersectingVideos.has(video)) return;
          showPlayBtn(video);
          video.play().catch(function () {});
        });
      },
      { signal: signal }
    );

    // --- Watchdog: detect frozen videos ---
    var lastKnownTimes = new Map();
    var watchdog = setInterval(function () {
      videos.forEach(function (video) {
        if (!intersectingVideos.has(video)) return;
        if (!loadedVideos.has(video)) return;
        if (prefersReducedMotion.matches) return;
        var prev = lastKnownTimes.get(video);
        var curr = video.currentTime;
        lastKnownTimes.set(video, curr);
        if (prev !== undefined && curr === prev && !confirmedPlaying.has(video)) {
          showPlayBtn(video);
          video.play().catch(function () {});
        }
      });
    }, 3000);

    // --- Cleanup ---
    function destroy() {
      controller.abort();
      observer.disconnect();
      clearInterval(watchdog);
    }

    // --- Theme editor integration ---
    document.addEventListener(
      'shopify:section:unload',
      function (e) {
        if (e.detail.sectionId === SECTION_ID) {
          destroy();
        }
      },
      { signal: signal }
    );

    document.addEventListener(
      'shopify:block:select',
      function (e) {
        if (e.detail.sectionId !== SECTION_ID) return;
        var block = e.target;
        if (block && block.scrollIntoView) {
          block.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }
      },
      { signal: signal }
    );
  }

  // --- Initialize all vertical-video sections on the page ---
  function initAll() {
    document.querySelectorAll('.vv-section[data-section-id]').forEach(function (el) {
      init(el.dataset.sectionId);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-initialize when a section is re-rendered in the theme editor
  document.addEventListener('shopify:section:load', function (e) {
    var el = document.querySelector('.vv-section[data-section-id="' + e.detail.sectionId + '"]');
    if (el) init(e.detail.sectionId);
  });
})();
